import React from "react";
import { observer } from "mobx-react";
import Calendar from "./Calendar";
import { useTimeSheetController } from "./controller/TimeSheetController";
import { CancelDayDialog } from "./CancelDayDialog";
import useStore from "../../helpers/useStore";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";

/**
 * CalendarWithController - A wrapper component that uses the CalendarController hook
 * to manage all calendar state and data, then passes the prepared props to the Calendar component.
 *
 * This component removes the need for parent components to manage calendar-specific state
 * and provides a clean separation between data management and UI rendering.
 */
const CalendarWithController: React.FC = observer(() => {
  const controller = useTimeSheetController();
  const calendarStore = useStore(CalendarContext);

  if (!controller) {
    return <div>Loading...</div>; // or some loading indicator
  }
  const { data, handlers } = controller;

  return (
    <>
      <Calendar
        // Core calendar data
        // Pre-adapted flat calendar events (new prop for refactor)
        calendarEvents={data.calendarEvents}
        // Patient and drawer data
        users={data.users}
        resources={data.resources}
        // UI state
        date={data.selectedDate}
        isLoading={data.isLoading}
        // Shelf events UI mode managed inside CalendarStore
        // isShelfEvents={calendarStore.isShelfEvents}
        lockedAppointments={data.lockedAppointments}
        headerWidth={data.headerWidth}
        // Timesheet mode flag for conditional modal behavior
        isTimesheetMode={calendarStore.timeSheetMode}
        // Event handlers
        onDatesSet={handlers.onDatesSet}
        onCreateEvent={handlers.onCreateEvent}
        onChangeEvent={handlers.onChangeEvent as any}
        onRemoveEvent={handlers.onRemoveEvent}
        onRemoveWorkingHours={handlers.onRemoveWorkingHours}
        onOutdated={handlers.onOutdated}
        onContinuousViewChange={() => {}}
        onCancelledViewChange={() => {}}
        onMoveEventDate={handlers.onMoveEventDate as any}
        onAddShelfEvent={handlers.onAddShelfEvent as any}
        onEventContentClick={handlers.onEventContentClick}
        onCloseNotesDialog={handlers.onCloseNotesDialog}
        onSelectedShelfEventReceive={handlers.onSelectedShelfEventReceive}
        groupCheck={data.groupCheck}
        openWarningDialog={data.openWarningDialog}
        warningMessage={data.warningMessage}
        onCloseWarningDialog={handlers.onCloseWarningDialog}
        cellSelection={data.focusPosition || undefined}
        onCancelDayClick={handlers.onCancelDayClick}
      />
      <CancelDayDialog
        open={data.cancelDayDialog.open}
        userName={data.cancelDayDialog.userName || ""}
        date={data.cancelDayDialog.date || ""}
        appointmentCount={0}
        loading={data.cancelDayDialog.loading}
        onConfirm={handlers.onCancelDayConfirm}
        onCancel={handlers.onCancelDayCancel}
      />
    </>
  );
});
CalendarWithController.displayName = "CalendarWithController";
export default CalendarWithController;
